import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  LinearProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssessmentIcon from "@mui/icons-material/Assessment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Link, useParams } from "react-router-dom";
import api from "../../api";
import ProgressReportDialog from "./ProgressReportDialog";
import { formatScorePercent } from "../../utils/formatScorePercent";
import { canViewReview, reviewPagePath } from "./tutorReviewUtils";

const statusColor = (completed, label) => {
  if (completed) return "success";
  const l = String(label || "").toLowerCase();
  if (l.includes("progress")) return "warning";
  if (l.includes("not started") || l.includes("no items")) return "default";
  return "default";
};

const computeCategoryProgress = (rows) => {
  const total = rows.length;
  const completed = rows.filter((r) => r.is_completed).length;
  const pct = total > 0 ? Math.round((completed / total) * 1000) / 10 : 0;
  const inProgress = rows.some((r) => {
    if (r.is_completed) return false;
    const label = String(r.status_label || "").toLowerCase();
    return label.includes("progress") || Boolean(r.started_at);
  });
  let statusLabel = "Not started";
  if (total === 0) statusLabel = "No items";
  else if (completed === total) statusLabel = "Completed";
  else if (completed > 0 || inProgress) statusLabel = "In progress";

  return {
    total,
    completed,
    pct,
    statusLabel,
    isCompleted: total > 0 && completed === total,
  };
};

function ResourceCategoryAccordion({
  title,
  rows,
  emptyLabel,
  showAccessColumns = false,
  defaultExpanded = false,
  tableViewProps = {},
}) {
  const { total, completed, pct, statusLabel, isCompleted } = computeCategoryProgress(rows);

  return (
    <Accordion defaultExpanded={defaultExpanded} sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ sm: "center" }}
          sx={{ width: "100%", pr: 1 }}
        >
          <Typography fontWeight={600} sx={{ flex: 1 }}>
            {title}
          </Typography>
          <Chip
            size="small"
            label={statusLabel}
            color={statusColor(isCompleted, statusLabel)}
          />
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 90 }}>
            {completed}/{total} items · {pct}%
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, pct))}
          sx={{ mb: 2, height: 8, borderRadius: 1 }}
        />
        <ResourceTable
          showAccessColumns={showAccessColumns}
          rows={rows}
          emptyLabel={emptyLabel}
          {...tableViewProps}
        />
      </AccordionDetails>
    </Accordion>
  );
}

const accessColor = (label) => {
  const l = String(label || "").toLowerCase();
  if (l === "free") return "success";
  if (l === "paid") return "primary";
  if (l === "course") return "secondary";
  return "default";
};

const AREA_BUCKETS = [
  { key: "weak_areas", label: "Weak areas", short: "Weak", color: "error" },
  { key: "medium_areas", label: "Medium areas", short: "Medium", color: "warning" },
  { key: "best_areas", label: "Best areas", short: "Best", color: "success" },
];

function ResourceTable({ rows, emptyLabel, buyerId, showAccessColumns = false }) {
  if (!rows?.length) {
    return (
      <Typography color="text.secondary" sx={{ py: 1 }}>
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Resource</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
            {showAccessColumns && (
              <>
                <TableCell sx={{ fontWeight: 600 }}>Access</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Platform</TableCell>
              </>
            )}
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Completed</TableCell>
            <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }} align="right">
              Score %
            </TableCell>
            {buyerId && (
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Action
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.type_label || row.type || "—"}</TableCell>
              {showAccessColumns && (
                <>
                  <TableCell>
                    {row.access_label ? (
                      <Chip
                        size="small"
                        label={row.access_label}
                        color={accessColor(row.access_type || row.access_label)}
                        variant="outlined"
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {row.platform_label ? (
                      <Chip
                        size="small"
                        label={row.platform_label}
                        color={row.platform === "mobile" ? "info" : "default"}
                        variant="outlined"
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </>
              )}
              <TableCell>
                <Chip
                  size="small"
                  label={row.status_label || (row.is_completed ? "Completed" : "Not completed")}
                  color={statusColor(row.is_completed, row.status_label)}
                />
              </TableCell>
              <TableCell>{row.completed_at || "—"}</TableCell>
              <TableCell
                align="right"
                sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}
              >
                {formatScorePercent(row)}
              </TableCell>
              {buyerId && (
                <TableCell align="right">
                  {canViewReview(row) ? (
                    <Button
                      size="small"
                      variant="outlined"
                      component={Link}
                      to={reviewPagePath(buyerId, row)}
                      state={{ row }}
                      startIcon={<VisibilityIcon />}
                    >
                      View
                    </Button>
                  ) : row.is_completed ? (
                    <Typography variant="caption" color="text.secondary">
                      No review data
                    </Typography>
                  ) : (
                    "—"
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function AreaInsightsPanel({ insights }) {
  const buckets = AREA_BUCKETS.map((bucket) => ({
    ...bucket,
    rows: Array.isArray(insights?.[bucket.key]) ? insights[bucket.key] : [],
  }));

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
        Performance by topic area
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Weak, medium, and best areas from completed papers, mock exams, and worksheets.
      </Typography>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems="stretch"
      >
        {buckets.map((bucket) => (
          <Box key={bucket.key} sx={{ flex: 1, minWidth: 0 }}>
            <Chip
              size="small"
              color={bucket.color}
              label={bucket.label}
              sx={{ mb: 1, fontWeight: 600 }}
            />
            {bucket.rows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No {bucket.short.toLowerCase()} areas yet.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {bucket.rows.slice(0, 8).map((row, idx) => (
                  <Box
                    key={`${bucket.key}-${row.subtopic_id || row.topic_id || idx}`}
                    sx={{
                      p: 1.25,
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {row.subtopic_name || row.topic_name || "Area"}
                    </Typography>
                    {row.topic_name && row.subtopic_name && row.topic_name !== row.subtopic_name && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {row.topic_name}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {Math.round(Number(row.avg_percentage) || 0)}% avg
                      {row.attempts != null ? ` · ${row.attempts} attempt${row.attempts === 1 ? "" : "s"}` : ""}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

export default function TutorStudentResources() {
  const { buyerId } = useParams();
  const [data, setData] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`tutor/students/${buyerId}/resources`);
        if (!cancelled) {
          if (res.data?.success) {
            setData(res.data.data);
          } else {
            setError(res.data?.message || "Failed to load student resources");
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || "Failed to load student resources");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [buyerId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        <Button
          component={Link}
          to="/tutor/students"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
        >
          Back to students
        </Button>
        <Typography color="error">{error || "Student not found"}</Typography>
      </Box>
    );
  }

  const student = data.student || {};
  const courses = data.courses || [];
  const worksheets = data.worksheets || [];
  const papers = data.papers || [];
  const hasPaperTypeSplit = papers.some(
    (p) => p.type === "past_paper" || p.type === "paper"
  );
  const pastPapers = hasPaperTypeSplit
    ? papers.filter((p) => p.type === "past_paper" || p.type_label === "Past paper")
    : papers;
  const practicePapers = hasPaperTypeSplit
    ? papers.filter((p) => p.type === "paper" || p.type_label === "Paper")
    : [];
  const mocks = data.mock_exams || [];
  const isMobileAssigned = Boolean(data.is_mobile_assigned ?? data.focus_mobile_resources);
  const hasMobileResources = Boolean(
    data.has_mobile_resources ||
      worksheets.length > 0 ||
      papers.some((p) => p.platform === "mobile") ||
      mocks.some((m) => m.platform === "mobile")
  );
  const areaInsights = data.area_insights || null;
  const tableViewProps = { buyerId };

  const worksheetRows = worksheets.map((w) => ({
    ...w,
    type_label: w.type_label || "Worksheet",
  }));
  const pastPaperRows = pastPapers.map((p) => ({
    ...p,
    type_label: p.type_label || "Past paper",
  }));
  const practicePaperRows = practicePapers.map((p) => ({
    ...p,
    type_label: p.type_label || "Paper",
  }));
  const mockRows = mocks.map((m) => ({ ...m, type_label: "Mock exam" }));

  return (
    <Box>
      <Button
        component={Link}
        to="/tutor/students"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2 }}
      >
        Back to students
      </Button>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 0.5 }}
      >
        <Typography variant="h5" fontWeight={700}>
          {student.name || "Student"}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AssessmentIcon />}
          onClick={() => setReportOpen(true)}
        >
          Send progress report
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap">
        {student.email && (
          <Typography color="text.secondary">{student.email}</Typography>
        )}
        {student.school_year && (
          <Chip size="small" label={student.school_year} />
        )}
        {isMobileAssigned && (
          <Chip size="small" color="info" label="Mobile assigned" />
        )}
        {hasMobileResources && (
          <Chip size="small" color="info" variant="outlined" label="Mobile activity" />
        )}
      </Stack>

      <AreaInsightsPanel insights={areaInsights} />

      <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
        Free & paid resources
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Past papers, practice papers, mock exams, and mobile worksheets — including free resources and anything accessed on mobile or web.
      </Typography>
      <Box sx={{ mb: 3 }}>
        <ResourceCategoryAccordion
          title="Worksheets (mobile)"
          rows={worksheetRows}
          showAccessColumns
          emptyLabel="No mobile worksheet attempts yet."
          defaultExpanded={worksheetRows.length > 0}
          tableViewProps={tableViewProps}
        />
        <ResourceCategoryAccordion
          title="Past papers"
          rows={pastPaperRows}
          showAccessColumns
          emptyLabel="No past papers for this student yet."
          defaultExpanded={pastPaperRows.length > 0}
          tableViewProps={tableViewProps}
        />
        {hasPaperTypeSplit && (
          <ResourceCategoryAccordion
            title="Practice papers"
            rows={practicePaperRows}
            showAccessColumns
            emptyLabel="No practice papers for this student yet."
            defaultExpanded={practicePaperRows.length > 0}
            tableViewProps={tableViewProps}
          />
        )}
        <ResourceCategoryAccordion
          title="Mock exams"
          rows={mockRows}
          showAccessColumns
          emptyLabel="No mock exams for this student yet."
          defaultExpanded={mockRows.length > 0}
          tableViewProps={tableViewProps}
        />
      </Box>

      <Typography variant="h6" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
        Assigned courses & resources
      </Typography>
      {courses.length === 0 ? (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography color="text.secondary">No courses assigned on your account.</Typography>
        </Paper>
      ) : (
        courses.map((course) => (
          <ResourceCategoryAccordion
            key={course.msr_id || course.course_id}
            title={course.course_name}
            rows={(course.items || []).map((item) => ({
              ...item,
              type_label: item.type_label,
            }))}
            emptyLabel="No topic / test resources recorded for this course yet."
            defaultExpanded
            tableViewProps={tableViewProps}
          />
        ))
      )}

      <ProgressReportDialog
        open={reportOpen}
        buyerId={buyerId}
        studentName={student.name || "this student"}
        onClose={() => setReportOpen(false)}
      />
    </Box>
  );
}
